import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { API_BASE } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, Clock, Target, Flame, ArrowRight, 
  CheckCircle2, XCircle, Loader2, Crown, Medal,
  Calendar, TrendingUp, Award
} from 'lucide-react';

const DailyChallengePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('challenge'); // challenge, leaderboard, history

  useEffect(() => {
    fetchChallenge();
    fetchStats();
  }, []);

  const fetchChallenge = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/daily-challenge/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(data);
      
      // If already submitted, show results
      if (data.user_status?.submitted) {
        setShowResults(true);
        setResults({
          score: data.user_status.score,
          answers: data.user_status.answers
        });
      }
    } catch (err) {
      console.error('Error fetching challenge:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/daily-challenge/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/daily-challenge/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  };

  const handleSelectAnswer = (questionId, optionIndex) => {
    if (showResults) return;
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const handleSubmit = async () => {
    if (!challenge) return;
    
    const answers = challenge.questions.map(q => ({
      question_id: q.id,
      selected_option: selectedAnswers[q.id] ?? -1
    }));

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${API_BASE}/daily-challenge/submit`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(data);
      setShowResults(true);
      fetchLeaderboard();
      fetchStats();
    } catch (err) {
      console.error('Error submitting:', err);
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!challenge || challenge.questions?.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Challenge Generating...</h2>
            <p className="text-slate-600">Today's challenge is being prepared. Please check back in a few minutes!</p>
            <Button className="mt-4" onClick={fetchChallenge}>Refresh</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const question = challenge.questions[currentQuestion];
  const answeredCount = Object.keys(selectedAnswers).length;
  const progress = (answeredCount / challenge.total_questions) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4" data-testid="daily-challenge-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-7 h-7 text-indigo-500" />
              Daily Challenge
            </h1>
            <p className="text-slate-600">{challenge.subject_name} • {challenge.date}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={view === 'challenge' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('challenge')}
            >
              Challenge
            </Button>
            <Button
              variant={view === 'leaderboard' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setView('leaderboard'); fetchLeaderboard(); }}
            >
              <Trophy className="w-4 h-4 mr-1" />
              Leaderboard
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <Flame className="w-5 h-5 text-orange-500 mx-auto" />
              <div className="text-xl font-bold">{stats.current_streak}</div>
              <div className="text-xs text-slate-500">Streak</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <TrendingUp className="w-5 h-5 text-green-500 mx-auto" />
              <div className="text-xl font-bold">{stats.average_score}%</div>
              <div className="text-xs text-slate-500">Avg Score</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <Award className="w-5 h-5 text-purple-500 mx-auto" />
              <div className="text-xl font-bold">{stats.best_score}%</div>
              <div className="text-xs text-slate-500">Best</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <Calendar className="w-5 h-5 text-blue-500 mx-auto" />
              <div className="text-xl font-bold">{stats.total_challenges}</div>
              <div className="text-xs text-slate-500">Total</div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      {view === 'challenge' && (
        <div className="max-w-4xl mx-auto">
          {/* Results View */}
          {showResults && results ? (
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className={`text-6xl font-bold ${results.score >= 70 ? 'text-green-500' : results.score >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {results.score}%
                  </div>
                  <p className="text-slate-600 mt-2">
                    {results.correct_count}/{results.total_questions} correct
                  </p>
                  {results.xp_earned && (
                    <Badge className="mt-2 bg-purple-500">+{results.xp_earned} XP</Badge>
                  )}
                </div>

                {/* Question Review */}
                <div className="space-y-4 mt-6">
                  <h3 className="font-semibold text-lg">Review Answers</h3>
                  {challenge.questions.map((q, idx) => {
                    const result = results.results?.find(r => r.question_id === q.id) || {};
                    return (
                      <div key={q.id} className={`p-4 rounded-lg border ${result.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start gap-2">
                          {result.is_correct ? 
                            <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" /> : 
                            <XCircle className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                          }
                          <div className="flex-1">
                            <p className="font-medium whitespace-pre-line">{idx + 1}. {q.question}</p>
                            <div className="mt-2 text-sm">
                              <p className="text-green-700">✓ Correct: {q.options[result.correct]}</p>
                              {!result.is_correct && (
                                <p className="text-red-700">✗ Your answer: {q.options[result.selected]}</p>
                              )}
                            </div>
                            {q.explanation && (
                              <p className="text-sm text-slate-600 mt-2 bg-white p-2 rounded whitespace-pre-line">{q.explanation}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Question View */
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">Question {currentQuestion + 1}/{challenge.total_questions}</Badge>
                  {!challenge.is_pro && challenge.total_questions < 10 && (
                    <Badge className="bg-amber-500">PRO: Get 10 Questions</Badge>
                  )}
                </div>
                <Progress value={progress} className="h-2 mt-2" />
              </CardHeader>
              <CardContent className="p-6">
                <h2 className="text-lg font-medium mb-4 whitespace-pre-line">{question?.question}</h2>
                
                <div className="space-y-3">
                  {question?.options?.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectAnswer(question.id, idx)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedAnswers[question.id] === idx
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                      <span className="whitespace-pre-line">{option}</span>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="mt-6 space-y-4">
                  {/* Question number dots - scrollable on mobile */}
                  <div className="flex justify-center">
                    <div className="flex gap-1 flex-wrap justify-center max-w-full">
                      {challenge.questions.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentQuestion(idx)}
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium transition-all ${
                            idx === currentQuestion
                              ? 'bg-indigo-500 text-white'
                              : selectedAnswers[challenge.questions[idx].id] !== undefined
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Prev/Next/Submit buttons */}
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                      disabled={currentQuestion === 0}
                      className="text-xs sm:text-sm"
                    >
                      <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                      Prev
                    </Button>
                    
                    <span className="text-xs sm:text-sm text-slate-500">
                      {answeredCount}/{challenge.total_questions} answered
                    </span>

                    {currentQuestion < challenge.total_questions - 1 ? (
                      <Button 
                        size="sm"
                        onClick={() => setCurrentQuestion(prev => prev + 1)}
                        className="text-xs sm:text-sm"
                      >
                        Next <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button 
                        size="sm"
                        onClick={handleSubmit}
                        disabled={submitting || answeredCount < challenge.total_questions}
                        className="bg-green-500 hover:bg-green-600 text-xs sm:text-sm"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Submit
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Leaderboard View */}
      {view === 'leaderboard' && (
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Today's Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard ? (
                <div className="space-y-2">
                  {leaderboard.leaderboard?.map((entry, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        entry.user_id === user?.id ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-white' :
                          'bg-slate-200 text-slate-600'
                        }`}>
                          {idx < 3 ? <Medal className="w-4 h-4" /> : entry.rank}
                        </div>
                        <span className="font-medium">{entry.name}</span>
                        {entry.user_id === user?.id && <Badge variant="outline">You</Badge>}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{entry.score}%</div>
                        <div className="text-xs text-slate-500">{entry.correct_count}/{entry.total_questions}</div>
                      </div>
                    </div>
                  ))}
                  
                  {leaderboard.total_participants > 10 && (
                    <p className="text-center text-sm text-slate-500 mt-4">
                      Showing top 10 of {leaderboard.total_participants} participants
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading leaderboard...
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DailyChallengePage;
